import { useMemo } from 'react';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import { fetchVesting, type VestingResponse } from '../lib/minerApi.js';

export interface VestingAggregateEntry {
  readonly total: string;
  readonly unlocked: string;
  readonly locked: string;
  readonly isLoading: boolean;
}

export interface VestingAggregate {
  /** Sum of all addresses' total balance in ngonka (string, BigInt-safe). */
  readonly total: string;
  /** Sum of unlocked / spendable across addresses. */
  readonly unlocked: string;
  /** Sum of locked / vesting across addresses. */
  readonly locked: string;
  /** True while at least one per-address query is still loading. */
  readonly isLoading: boolean;
  /** First error encountered, if any. */
  readonly error: unknown;
  /** Per-address split so wallet rows can show their own total/unlocked/locked. */
  readonly perAddress: Map<string, VestingAggregateEntry>;
}

/**
 * Parallel fetch /api/public/vesting/{addr}/ for every wallet, then
 * sum the ngonka strings via BigInt to avoid float precision loss.
 *
 * Returns ngonka strings so HeroCard consumers can call
 * formatGnkDisplay(...) to render them at full chain precision.
 * The 'total' field is spendable + locked, matching the gonka_miniapp
 * Total Balance line.
 */
export function useVestingAggregate(
  addresses: readonly string[],
): VestingAggregate {
  const queries = useQueries({
    queries: addresses.map((addr) => ({
      queryKey: ['vesting', addr] as const,
      queryFn: async (): Promise<VestingResponse | null> => {
        if (!addr) return null;
        return fetchVesting(addr);
      },
      enabled: !!addr,
      staleTime: 60_000,
      retry: 1,
    })),
  }) as UseQueryResult<VestingResponse | null, Error>[];

  return useMemo((): VestingAggregate => {
    let totalSum = 0n;
    let unlockedSum = 0n;
    let lockedSum = 0n;
    let anyLoading = false;
    let firstError: unknown = null;
    const perAddress = new Map<string, VestingAggregateEntry>();

    for (let i = 0; i < queries.length; i++) {
      const q = queries[i]!;
      const addr = addresses[i];
      const entryLoading = q.isLoading || q.isFetching;
      if (entryLoading) anyLoading = true;
      if (q.error && !firstError) firstError = q.error;
      const data = q.data;
      let entryTotal = '0';
      let entryUnlocked = '0';
      let entryLocked = '0';
      if (data) {
        try {
          const t = BigInt(data.total_ngonka || '0');
          const u = BigInt(data.unlocked_ngonka || '0');
          const l = BigInt(data.locked_ngonka || '0');
          totalSum += t;
          unlockedSum += u;
          lockedSum += l;
          entryTotal = t.toString();
          entryUnlocked = u.toString();
          entryLocked = l.toString();
        } catch {
          /* skip malformed */
        }
      }
      if (addr) {
        perAddress.set(addr, {
          total: entryTotal,
          unlocked: entryUnlocked,
          locked: entryLocked,
          isLoading: entryLoading,
        });
      }
    }

    return {
      total: totalSum.toString(),
      unlocked: unlockedSum.toString(),
      locked: lockedSum.toString(),
      isLoading: anyLoading,
      error: firstError,
      perAddress,
    };
  }, [queries, addresses]);
}
